import { Box, CircularProgress, IconButton, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { useSelector, useDispatch } from "react-redux";
import { preparePopupGraphData } from "./quotaModificationUtils";
import { setState } from "./quotaModificationSlice";
import CloseIcon from "@mui/icons-material/Close";
import MultiSelectProductsQuotaModification from "./MultiSelectProductsQuotaModification";

export default function MultilineChart() {
	const dispatch = useDispatch();
	const state = useSelector((store) => store.quotaModification);
	const {
		popupGraphData,
		popupGraphDataLoading = false,
		graphModalScenarioName,
		graphModalCountryName,
		graphModalSkuPgsData,
	} = state;

	const [graphData, setGraphData] = useState({
		periods: [],
		seriesData: [],
		colors: [],
	});

	useEffect(() => {
		if (!popupGraphData || !Array.isArray(popupGraphData)) {
			setGraphData({ periods: [], seriesData: [], colors: [] });
			return;
		}
		const data = preparePopupGraphData(popupGraphData);
		setGraphData({
			periods: data.periods,
			seriesData: data.seriesData,
			colors: data.color,
		});
	}, [popupGraphData]);

	const chartTitle =
		graphModalCountryName || graphModalScenarioName
			? [graphModalCountryName, graphModalScenarioName].filter(Boolean).join(" — ")
			: "Data Sources";

	const options = {
		chart: {
			toolbar: {
				show: true,
			},
		},
		title: {
			text: "",
		},
		tooltip: {
			y: {
				formatter: (value) => {
					const formatter = new Intl.NumberFormat("en-US");
					return formatter.format(value);
				},
			},
		},
		xaxis: {
			categories: [...graphData.periods],
		},
		yaxis: {
			labels: {
				formatter: (value) => {
					const formatter = new Intl.NumberFormat("en-US");
					return formatter.format(value);
				},
			},
		},
		stroke: {
			width: 2,
			curve: "smooth",
		},
		markers: {
			size: 4,
		},
		colors: graphData.colors,
	};
	const series = [...graphData.seriesData];

	function handleClose() {
		dispatch(setState("graphModalOpen", false));
		dispatch(setState("popupGraphDataLoading", false));
		dispatch(setState("popupGraphData", null));
		dispatch(setState("graphModalScenarioName", ""));
		dispatch(setState("graphModalCountryName", ""));
		dispatch(setState("graphModalScenarioId", null));
		dispatch(setState("graphModalSkuPgsData", []));
		dispatch(setState("graphModalSelectedProductIds", []));
	}

	return (
		<Box>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					gap: 1,
					marginBottom: "8px",
					pr: 0.5,
					minHeight: 44,
				}}
			>
				<Typography
					variant="subtitle1"
					fontWeight={600}
					sx={{ flexShrink: 0, maxWidth: "38%", lineHeight: 1.25 }}
				>
					{chartTitle}
				</Typography>
				<Box
					sx={{
						flex: 1,
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						minWidth: 0,
					}}
				>
					<MultiSelectProductsQuotaModification skuPgs={graphModalSkuPgsData} />
				</Box>
				<IconButton
					onClick={handleClose}
					aria-label="close chart"
					sx={{ flexShrink: 0 }}
				>
					<CloseIcon />
				</IconButton>
			</Box>
			<Box sx={{ position: "relative", width: 780, height: 500 }}>
				<Chart
					options={options}
					series={series}
					type="line"
					height={500}
					width={780}
				/>
				{popupGraphDataLoading ? (
					<Box
						sx={{
							position: "absolute",
							inset: 0,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							backgroundColor: (theme) =>
								theme.palette.mode === "dark"
									? "rgba(0,0,0,0.55)"
									: "rgba(255,255,255,0.75)",
							zIndex: 1,
						}}
					>
						<CircularProgress aria-label="Loading chart data" />
					</Box>
				) : null}
			</Box>
		</Box>
	);
}
